import React from 'react';
import styled from 'styled-components';
import { isColorDark } from 'src/dispensary-admin/customize/helpers';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { MobileNavigationStyle } from 'types/graphql';

type TabBarProps = {
  backgroundColor?: string | null;
  logo?: string | null;
  navigationStyle?: MobileNavigationStyle | null;
};

const MenuIcon = (): JSX.Element => (
  <svg width='20' height='20' viewBox='0 0 20 20' fill='none' xmlns='http://www.w3.org/2000/svg'>
    <rect y='3' width='20' height='2' fill='currentColor' />
    <rect y='9' width='20' height='2' fill='currentColor' />
    <rect y='15' width='20' height='2' fill='currentColor' />
  </svg>
);

const valueOrDefault = (value: string | null | undefined, defaultValue: string): string =>
  value?.length ? value : defaultValue;

const isSolidStyle = (navigationStyle?: MobileNavigationStyle | null): boolean =>
  navigationStyle === MobileNavigationStyle.solid;

const MobileHeader = ({ backgroundColor, logo, navigationStyle }: TabBarProps): JSX.Element => {
  const flags = useFlags();
  const showRedesign = flags['ecomm.app.v2-redesign'] ?? false;
  // When the flag is off: v1 always renders a solid header (ignoring
  // navigationStyle) and v2 never renders a header background at all.
  // Behind the flag: Solid is the only style that renders a solid header
  // background — Liquid Glass floats over content and ignores
  // headerBackgroundColor, and Platform Default renders using the Liquid
  // Glass display in this preview (the admin can't know the end-user's OS,
  // so it mirrors the modern default).
  const advancedCustomizationEnabled = flags['ecomm.app.advanced-customization'] ?? false;
  const isSolid = isSolidStyle(navigationStyle);
  const isLiquidGlass = advancedCustomizationEnabled && !isSolid;
  // Header background color is configured for other app screens, but the storefront header
  // itself remains transparent for every advanced style (including Solid).
  const showsSolidBackground = !advancedCustomizationEnabled;
  // Elevation still distinguishes the new Solid style.
  const showsElevation = advancedCustomizationEnabled && isSolid;

  if (showRedesign) {
    return (
      <MobileHeaderContainerV2 $showsElevation={showsElevation}>
        {!!logo?.length && (
          <LogoV2>
            <img src={logo} alt='Dispensary Name' />
          </LogoV2>
        )}
        <NameV2>Dispensary Name</NameV2>
      </MobileHeaderContainerV2>
    );
  }

  return (
    <MobileHeaderContainer
      $isLiquidGlass={isLiquidGlass}
      $showsSolidBackground={showsSolidBackground}
      $showsElevation={showsElevation}
      backgroundColor={valueOrDefault(backgroundColor, '#3387E1')}
      useLightText={showsSolidBackground && isColorDark(valueOrDefault(backgroundColor, '#3387E1'))}
    >
      <MenuIcon />
      {!!logo?.length && (
        <Logo>
          <img src={logo} alt='Dispensary Name' />
        </Logo>
      )}
      {!logo?.length && <Name>Dispensary Name</Name>}
    </MobileHeaderContainer>
  );
};

const MobileHeaderContainer = styled.div<{
  $isLiquidGlass: boolean;
  $showsSolidBackground: boolean;
  $showsElevation: boolean;
  backgroundColor: string;
  useLightText: boolean;
}>`
  align-items: center;
  background-color: ${({ $showsSolidBackground, backgroundColor }) =>
    $showsSolidBackground ? backgroundColor : 'transparent'};
  backdrop-filter: ${({ $isLiquidGlass }) => ($isLiquidGlass ? 'blur(20px) saturate(180%)' : 'none')};
  box-shadow: ${({ $showsElevation }) => ($showsElevation ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none')};
  // Advanced headers float over the app canvas, so choose text against that canvas.
  // The legacy v1 header still paints its configured background. See ECOMM-12580.
  color: ${({ $showsSolidBackground, useLightText, theme }) => {
    if (!$showsSolidBackground) {
      return theme?.isBackgroundDark ? '#ffffff' : '#2c3236';
    }
    return useLightText ? '#ffffff' : '#2c3236';
  }};
  display: flex;
  padding: 40px 16px 10px;
  width: 100%;

  > svg {
    margin-right: 15px;
  }
`;

const MobileHeaderContainerV2 = styled.div<{ $showsElevation: boolean }>`
  background-color: transparent;
  box-shadow: ${({ $showsElevation }) => ($showsElevation ? '0 2px 8px rgba(0, 0, 0, 0.12)' : 'none')};
  // The header sits directly on the app canvas for every style.
  color: ${({ theme }) => (theme?.isBackgroundDark ? '#ffffff' : 'inherit')};
  padding: 40px 16px 10px;
  width: 100%;
`;

const Name = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const NameV2 = styled.div`
  color: inherit;
  font-size: 24px;
  font-weight: bold;
`;

const Logo = styled.div`
  flex: 1 0 0px;
  text-align: center;

  img {
    margin-left: -30px;
    max-height: 34px;
    width: auto;
  }
`;

const LogoV2 = styled.div`
  img {
    max-height: 50px;
    width: auto;
  }
`;

export default MobileHeader;
